import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { ChevronDown } from "lucide-react";
import { navLinks, getDashboardPath } from "./navConfig";

const MobileNav = ({ open, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;
  const toggle = (name) => setExpandedSections((prev) => ({ ...prev, [name]: !prev[name] }));

  if (!open) return null;

  return (
    <div className="md:hidden py-4 border-t border-slate-100 animate-fade-in">
      <div className="flex flex-col space-y-1">
        {navLinks.map((link) =>
          link.children ? (
            <div key={link.name}>
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-rnb-navy hover:bg-slate-50 rounded-lg transition-colors"
                onClick={() => toggle(link.name)}
                data-testid={`mobile-toggle-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
                <ChevronDown className={`h-4 w-4 text-rnb-gray transition-transform duration-200 ${expandedSections[link.name] ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections[link.name] ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="pb-2">
                  {link.children.map((child) =>
                    child.children ? (
                      <div key={child.path || child.name}>
                        {child.collapsible ? (
                          <>
                            <button
                              className="w-full flex items-center justify-between px-8 py-2 text-sm font-medium text-rnb-navy hover:bg-slate-50 transition-colors"
                              onClick={() => toggle(`mobile-${child.name}`)}
                              onDoubleClick={() => { if (child.path) { navigate(child.path); onClose(); } }}
                            >
                              {child.name}
                              <ChevronDown className={`h-3 w-3 text-rnb-gray transition-transform duration-200 ${expandedSections[`mobile-${child.name}`] ? "rotate-180" : ""}`} />
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections[`mobile-${child.name}`] ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                              {child.children.map((sub) => (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className="block px-12 py-2 text-sm text-rnb-gray hover:text-rnb-blue hover:bg-slate-50"
                                  onClick={onClose}
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <Link
                              to={child.path}
                              className="block px-8 py-2 text-sm font-medium text-rnb-navy hover:text-rnb-blue hover:bg-slate-50"
                              onClick={onClose}
                            >
                              {child.name}
                            </Link>
                            {child.children.map((sub) => (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                className="block px-12 py-2 text-sm text-rnb-gray hover:text-rnb-blue hover:bg-slate-50"
                                onClick={onClose}
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-8 py-2 text-sm text-rnb-gray hover:text-rnb-blue hover:bg-slate-50"
                        onClick={onClose}
                      >
                        {child.name}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
          ) : link.external ? (
            <a
              key={link.path}
              href={link.path}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 text-sm font-medium rounded-lg text-rnb-gray hover:text-rnb-blue hover:bg-slate-50"
              onClick={onClose}
            >
              {link.name}
            </a>
          ) : (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg ${
                isActive(link.path) ? "text-rnb-blue bg-rnb-light" : "text-rnb-gray hover:text-rnb-blue hover:bg-slate-50"
              }`}
              onClick={onClose}
            >
              {link.name}
            </Link>
          )
        )}
        <div className="pt-4 px-4 space-y-2">
          {user ? (
            <>
              <Link to={getDashboardPath(user.role)} onClick={onClose}>
                <Button variant="outline" className="w-full">Dashboard</Button>
              </Link>
              <Button variant="destructive" className="w-full" onClick={() => { logout(); onClose(); }}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={onClose}>
                <Button variant="outline" className="w-full">Sign In</Button>
              </Link>
              <Link to="/contact" onClick={onClose}>
                <Button className="w-full">Book Consultation</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileNav;
